import {
    Chip,
    IconButton,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Stack,
    Typography,
} from '@mui/material';
import { useDateFormatter } from '../../utils/date';
import { MouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import { FileMetadata } from '../../api/types/cms';
import {
    CloudDownloadOutlined,
    DeleteOutlined,
    FileOutlined,
} from '@ant-design/icons';
import { useTheme } from '@mui/material/styles';
import { useImageBuilder } from '../../utils/image';
import { getReadableFileSizeString } from '../../utils/static-files';
import { useTranslation } from '../../contexts/TranslationContext';

type FileItemProps = {
    file: FileMetadata;
    actionsEnabled: boolean;
    onItemClick?: (selectedFile: FileMetadata) => void;
    onDelete?: (selectedFile: FileMetadata) => void;
    active?: boolean;
};

export default function ListFileItem({
    file,
    actionsEnabled,
    onItemClick,
    onDelete,
    active = false,
}: FileItemProps) {
    const itemRef = useRef<HTMLDivElement>();
    const { buildImageUrl, buildDownloadUrl } = useImageBuilder();
    const { formatDate } = useDateFormatter();
    const { translate } = useTranslation();

    const theme = useTheme();
    const [size, setSize] = useState({ width: 0, height: 0 });

    const onResize = (
        entries: ResizeObserverEntry[],
        observer: ResizeObserver,
    ) => {
        if (entries && entries[0]) {
            const element = entries[0];
            let height = 0;

            const ratio = file.additional_data?.ratio?.split(':');
            if (ratio && ratio[0] && ratio[1]) {
                height =
                    (element.target.clientWidth / parseInt(ratio[0])) *
                    parseInt(ratio[1]);
            }

            setSize({ width: element.target.clientWidth, height });
        }
    };

    useEffect(() => {
        if (itemRef.current) {
            const observer = new ResizeObserver(onResize);
            observer.observe(itemRef.current);

            return () => {
                observer.disconnect();
            };
        }
    }, [itemRef.current]);

    const openVersionList = () => {
        onItemClick && onItemClick(file);
    };

    const downloadFile = (event: MouseEvent) => {
        event.stopPropagation();
        window.open(buildDownloadUrl(file), '_blank');
    };

    const removeFile = (event: MouseEvent) => {
        event.stopPropagation();
        onDelete && onDelete(file);
    };

    return useMemo(() => {
        return (
            <ListItemButton
                key={file.id}
                data-key={file.id}
                onClick={openVersionList}
                disableRipple
                alignItems="center"
                dense
                selected={active}>
                {file.mime_type?.includes('image') ? (
                    <ListItemIcon
                        sx={{ alignItems: 'center', height: '100%', mr: 4 }}>
                        <img
                            src={`${buildImageUrl(file)}`}
                            style={{ width: 50, height: 'auto' }}
                            alt={file.file_id}
                        />
                    </ListItemIcon>
                ) : (
                    <ListItemIcon
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            height: '100%',
                            width: 50,
                            mr: 4,
                        }}>
                        <FileOutlined style={{ fontSize: 24 }} />
                    </ListItemIcon>
                )}
                <ListItemText
                    primary={
                        <Typography
                            variant="h5"
                            className="cutTextEnd"
                            sx={{ maxWidth: '75%' }}
                            // @ts-ignore
                            title={file.original_name ?? file.item_name ?? ''}>
                            {
                                // @ts-ignore
                                file.original_name ?? file.item_name
                            }
                        </Typography>
                    }
                    secondaryTypographyProps={{
                        component: 'div',
                    }}
                    secondary={
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography
                                variant="overline"
                                component="p">{`${file.mime_type}`}</Typography>
                            <Typography
                                variant="caption"
                                component="p">{`•`}</Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                component="p">
                                {`${translate('pages.cms.files.version')}: ${file.version}${file.created_at ? ` • ${formatDate(file.created_at)}` : ''}${file?.size ? ` • ${getReadableFileSizeString(file.size ?? 0)}` : ''}`}
                            </Typography>
                        </Stack>
                    }
                />
                {typeof file?.enabled !== 'undefined' && !file.enabled && (
                    <div>
                        <Chip
                            label={translate(
                                'pages.cms.files.warning_inactive',
                            )}
                            size="small"
                            color="warning"
                            sx={{ borderRadius: 4 }}
                        />
                    </div>
                )}
                {actionsEnabled && (
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="center"
                        zIndex={2}
                        sx={{ minWidth: 75 }}>
                        <IconButton color="inherit" onClick={downloadFile}>
                            <CloudDownloadOutlined />
                        </IconButton>
                        <IconButton color="error" onClick={removeFile}>
                            <DeleteOutlined />
                        </IconButton>
                    </Stack>
                )}
            </ListItemButton>
        );
    }, [file, size, active, theme]);
}
